import { useTranslation } from 'react-i18next';
import { useState, type FC } from 'react';
import {
  MANUAL_CLINIC_NOTES_PER_PAGE,
  useGetManualClinicNotes,
} from 'infrastructure/hooks/ccm';
import BaseTableNew from 'components/atoms/base-table/new';
import EmptyTablePlaceholder from 'components/atoms/base-empty-table-placeholder';
import { ModuleType } from 'infrastructure/enums';
import classNames from 'classnames';
import { useDeleteManualClinicNotes } from 'infrastructure/hooks/ccm/medical-activity/use-delete-manual-clinic-notes';
import { useModule } from 'infrastructure/providers/module-provider';
import { FileMarkdownOutlined } from '@ant-design/icons';

import s from './styles.module.scss';
import { columns } from './columns';
import ModuleTabs from '../module-tabs';

import type { SortState } from 'infrastructure/types';

interface ManualClinicalNotesLoggedProps {
  patientGuid: string;
  showTabs?: boolean;
  module?: ModuleType;
  showTitle?: boolean;
}

const ManualClinicalNotesLogged: FC<ManualClinicalNotesLoggedProps> = ({
  patientGuid,
  showTabs = false,
  module = ModuleType.CCM,
  showTitle = true,
}) => {
  const [page, setPage] = useState(1);
  const [documentationTimeSort, setDocumentationTimeSort] =
    useState<SortState>('');
  const [createdAtSort, setCreatedAtSort] = useState<SortState>('');

  const { module: internalModule, changeModule: setModuleType } =
    useModule(module);
  const moduleType = showTabs ? internalModule : module;

  const { manualClinicNotes, isLoading } = useGetManualClinicNotes({
    patientGuid,
    meta: { items: MANUAL_CLINIC_NOTES_PER_PAGE, page },
    module: moduleType,
    documentationTimeSort,
    createdAtSort,
  });

  const { deleteManualClinicNotesAsync } = useDeleteManualClinicNotes({
    patientGuid,
    meta: { items: MANUAL_CLINIC_NOTES_PER_PAGE, page },
    module: moduleType,
  });
  const { t } = useTranslation();

  return (
    <div className={s.wrapper}>
      <div className={classNames({ [s['title-wrapper']]: showTabs })}>
        {showTitle && (
          <h3 className={s.title}>{t('labels.manualClinicNotesLogged')}</h3>
        )}
        {showTabs && (
          <ModuleTabs
            activeKey={moduleType}
            className={s.tabs}
            onChange={(tab) => {
              setModuleType(tab.key);
              setPage(1);
              setDocumentationTimeSort('');
              setCreatedAtSort('');
            }}
          />
        )}
      </div>
      <BaseTableNew
        items={manualClinicNotes?.items ?? []}
        columns={columns({
          deleteNote: async (data) => {
            await deleteManualClinicNotesAsync({
              ...data,
              patientGuid,
              module: moduleType,
            });
          },
          documentationTimeSort,
          createdAtSort,
        })}
        bordered
        changeOddRowColor={false}
        rowHoverable={false}
        loading={isLoading}
        pagination={{
          current: page,
          pageSize: MANUAL_CLINIC_NOTES_PER_PAGE,
          total: manualClinicNotes?.count ?? 0,
          position: ['bottomRight'],
        }}
        onChangePage={(newPage) => {
          setPage(newPage);
        }}
        dataCy="manual-clinic-notes-table"
        emptyText={
          <EmptyTablePlaceholder
            text={t('labels.noManualClinicNotesLogged')}
            icon={<FileMarkdownOutlined />}
            column
          />
        }
        onSort={(field, order) => {
          if (!field || !order) {
            setCreatedAtSort('');
            setDocumentationTimeSort('');
          } else if (field === 'createdAt') {
            setCreatedAtSort(order);
            setDocumentationTimeSort('');
          } else if (field === 'documentationTime') {
            setDocumentationTimeSort(order);
            setCreatedAtSort('');
          }
        }}
      />
    </div>
  );
};
export default ManualClinicalNotesLogged;
